import { api, OfflineError } from './api.ts';
import { store } from './db.ts';
import { decryptWithNoteKey, encryptWithNoteKey, newId } from './notes.ts';
import type { LocalNote, LocalVersion } from './types.ts';

/**
 * Versionshistorie (docs/KONZEPT.md, Abschnitt 6). Eine Version ist der vollständige
 * verschlüsselte Markdown-Text — kein Delta. Dadurch bleibt sie lesbar, auch wenn der
 * Sync-Log längst kompaktiert wurde.
 */

/** Nach dieser Pause ohne Eingabe gilt ein Bearbeitungsabschnitt als abgeschlossen. */
export const VERSION_IDLE_MS = 5 * 60 * 1000;

export async function captureVersion(
  note: LocalNote,
  options: { labeled?: boolean; previousContent?: string } = {},
): Promise<LocalVersion | null> {
  const previous = options.previousContent ?? (await lastContent(note.id));
  if (previous !== null && previous === note.content) return null;

  const version: LocalVersion = {
    id: newId(),
    noteId: note.id,
    createdAt: Date.now(),
    charDelta: note.content.length - (previous?.length ?? 0),
    labeled: options.labeled ?? false,
    content: note.content,
    pushed: false,
  };
  await store.putVersion(version);
  void pushVersion(note, version).catch(() => undefined);
  return version;
}

async function lastContent(noteId: string): Promise<string | null> {
  const local = await store.versionsOf(noteId);
  return local[0]?.content ?? null;
}

export async function pushVersion(note: LocalNote, version: LocalVersion): Promise<void> {
  if (version.content === null) return;
  try {
    await api.postVersion(note.id, {
      id: version.id,
      payload: encryptWithNoteKey(version.content, note.wrappedDek),
      createdAt: version.createdAt,
      charDelta: version.charDelta,
      labeled: version.labeled,
    });
    await store.putVersion({ ...version, pushed: true });
  } catch (error) {
    if (error instanceof OfflineError) return; // bleibt liegen, wird später nachgereicht
    throw error;
  }
}

export async function pushPendingVersions(): Promise<void> {
  for (const version of await store.unpushedVersions()) {
    const note = await store.getNote(version.noteId);
    if (!note) continue;
    await pushVersion(note, version).catch(() => undefined);
  }
}

/**
 * Ausdünnung nach der Staffel aus dem Konzept: unter 24 Stunden alles, dann stündlich,
 * täglich, wöchentlich. Beschriftete Stände bleiben immer.
 */
function bucketOf(createdAt: number, now: number, id: string): string {
  const age = now - createdAt;
  if (age < 24 * 3_600_000) return `all:${id}`;
  if (age < 7 * 86_400_000) return `h:${Math.floor(createdAt / 3_600_000)}`;
  if (age < 90 * 86_400_000) return `d:${Math.floor(createdAt / 86_400_000)}`;
  return `w:${Math.floor(createdAt / (7 * 86_400_000))}`;
}

export async function thinVersions(noteId: string): Promise<string[]> {
  const all = await store.versionsOf(noteId); // absteigend nach Zeit
  const now = Date.now();
  const seen = new Set<string>();
  const drop: string[] = [];

  all.forEach((version, index) => {
    if (version.labeled || index === 0) return; // beschriftet und der neueste Stand bleiben
    const bucket = bucketOf(version.createdAt, now, version.id);
    if (seen.has(bucket)) drop.push(version.id);
    else seen.add(bucket);
  });

  if (drop.length > 0) {
    await store.removeVersions(drop);
    await api.pruneVersions(noteId, drop).catch(() => undefined);
  }
  return drop;
}

export type VersionEntry = {
  id: string;
  createdAt: number;
  charDelta: number;
  labeled: boolean;
  local: boolean;
};

/** Lokale und serverseitige Stände zusammengeführt — offline bleibt die lokale Liste. */
export async function listVersions(noteId: string): Promise<VersionEntry[]> {
  const local = await store.versionsOf(noteId);
  const merged = new Map<string, VersionEntry>();
  for (const version of local) {
    merged.set(version.id, {
      id: version.id,
      createdAt: version.createdAt,
      charDelta: version.charDelta,
      labeled: version.labeled,
      local: true,
    });
  }
  try {
    for (const meta of await api.listVersions(noteId)) {
      if (!merged.has(meta.id)) merged.set(meta.id, { ...meta, local: false });
    }
  } catch {
    // offline: die lokale Liste ist die Antwort
  }
  return [...merged.values()].sort((a, b) => b.createdAt - a.createdAt);
}

export async function loadVersionContent(note: LocalNote, versionId: string): Promise<string> {
  const local = (await store.versionsOf(note.id)).find((v) => v.id === versionId);
  if (local?.content !== null && local?.content !== undefined) return local.content;

  const remote = await api.getVersion(note.id, versionId);
  const content = decryptWithNoteKey(remote.payload, note.wrappedDek);
  await store.putVersion({
    id: versionId,
    noteId: note.id,
    createdAt: remote.createdAt,
    charDelta: 0,
    labeled: false,
    content,
    pushed: true,
  });
  return content;
}
